// Centralized product content for individual product detail pages.
// Each product entry feeds /products/:slug via ProductDetail.jsx.

export const products = {
  // ===== COMMERCIAL =====
  'general-liability': {
    slug: 'general-liability',
    category: 'commercial',
    name: 'General Liability Insurance',
    tagline: 'Guarding your business against unforeseen liabilities.',
    contactType: 'liability',
    heroImage: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=80',
    intro: [
      "The world of business can be unpredictable. Safeguard your enterprise's reputation and assets from potential threats with our comprehensive general liability insurance. Navigate uncertainties confidently, knowing you're backed by a policy that has your best interests at heart.",
      "Investing in general liability coverage is an investment in peace of mind. We help ensure that unexpected challenges remain manageable while your business stays focused on growth."
    ],
    features: [
      'Comprehensive protection that keeps your business resilient through unforeseen events.',
      'A straightforward quote process emphasizing transparency and clarity from the outset.',
      'Diverse coverage options tailored to fit your unique business needs and risk profile.',
      'Coverage for both bodily injury and property damage claims brought against your business.',
      'Protection against personal and advertising injury allegations.'
    ],
    sections: [
      {
        title: 'A Strong Foundation in a Changing Business Landscape',
        body: 'We recognize that each business has its unique challenges and requirements. Our general liability insurance is crafted to resonate with your specific needs, delivering a policy that aligns with your business essence.'
      },
      {
        title: 'All-Around Liability Coverage',
        body: 'While mishaps are unpredictable, your protection from their repercussions does not have to be. Our coverage extends to property damages, personal injury claims, and legal defense costs, giving you peace of mind.'
      },
      {
        title: 'Clarity in Pricing, Excellence in Protection',
        body: 'Quality protection should not strain your finances. We blend strong coverage with competitive pricing, ensuring you get genuine value for every dollar spent.'
      }
    ]
  },

  'commercial-property': {
    slug: 'commercial-property',
    category: 'commercial',
    name: 'Commercial Property Insurance',
    tagline: 'Protect the physical foundation of your business.',
    contactType: 'property',
    heroImage: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80',
    intro: [
      "Your building, equipment, inventory, and physical assets are the backbone of your operations. Commercial property insurance shields them from losses due to fire, theft, vandalism, severe weather, and other covered events.",
      "We help you rebuild and recover quickly when the unexpected happens — so a single incident does not derail your business momentum."
    ],
    features: [
      'Coverage for owned and leased buildings, structures, and improvements.',
      'Protection for business personal property: equipment, furniture, inventory, and supplies.',
      'Optional business interruption coverage to replace lost income during repairs.',
      'Coverage for losses caused by fire, theft, vandalism, storms, and more.',
      'Replacement cost and actual cash value options to match your needs.'
    ],
    sections: [
      {
        title: 'Comprehensive Property Protection',
        body: 'From small storefronts to large commercial facilities, our property coverage is structured around the real risks your industry faces — so you are protected where it matters most.'
      },
      {
        title: 'Business Income Continuity',
        body: 'When a covered event forces you to pause operations, business income coverage helps replace lost revenue and pay ongoing expenses while you rebuild.'
      },
      {
        title: 'Tailored to Your Industry',
        body: 'Retail, restaurants, contractors, offices, warehouses — every industry has unique exposures. We match the policy to your operations, not the other way around.'
      }
    ]
  },

  'workers-compensation': {
    slug: 'workers-compensation',
    category: 'commercial',
    name: "Workers' Compensation Insurance",
    tagline: 'Protect your team and meet state requirements with confidence.',
    contactType: 'workers-comp',
    heroImage: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80',
    intro: [
      "Workers compensation insurance provides essential coverage when an employee is injured or becomes ill on the job. It helps pay for medical care, lost wages, and rehabilitation while protecting your business from potentially costly lawsuits.",
      "In most states, workers comp is mandatory once you have employees. We help you stay compliant while building a workplace where your team feels protected."
    ],
    features: [
      'Coverage for medical expenses, lost wages, and rehabilitation costs after a work-related injury.',
      'Employer liability protection against employee injury lawsuits.',
      'Help meeting state-mandated coverage requirements in Florida and beyond.',
      'Return-to-work programs and claim management support.',
      'Disability and death benefit coverage for qualifying claims.'
    ],
    sections: [
      {
        title: 'Compliance Made Simple',
        body: 'Workers comp rules vary by state and industry. We guide you through the requirements, classification codes, and payroll reporting so you stay compliant without the headache.'
      },
      {
        title: 'Protecting Your Most Valuable Asset',
        body: 'Your employees are the heart of your business. Workers comp signals that you take their safety seriously and provides real financial support if the unexpected happens.'
      },
      {
        title: 'Manage Costs with Smart Strategies',
        body: 'Through proper classification, safety programs, and claim management, we help you control premiums while maintaining strong coverage.'
      }
    ]
  },

  'commercial-auto': {
    slug: 'commercial-auto',
    category: 'commercial',
    name: 'Commercial Auto Insurance',
    tagline: 'Coverage for the vehicles that keep your business moving.',
    contactType: 'commercial',
    heroImage: 'https://customer-assets.emergentagent.com/job_insure-your-future/artifacts/g5rg75jr_smiley-delivery-man-handing-package.jpg',
    intro: [
      "If your business owns or operates vehicles — company cars, delivery vans, service trucks, or a full fleet — personal auto policies will not cut it. Commercial auto insurance protects your vehicles, drivers, and operations.",
      "From a single van to a multi-vehicle fleet, we structure coverage that fits how your business actually uses the road."
    ],
    features: [
      'Liability coverage for bodily injury and property damage caused by your business vehicles.',
      'Collision and comprehensive coverage for vehicle damage from accidents, theft, fire, and weather.',
      'Medical payments and uninsured/underinsured motorist protection.',
      'Hired and non-owned auto coverage for employees driving personal or rental vehicles for work.',
      'Customized fleet programs for businesses with multiple vehicles.'
    ],
    sections: [
      {
        title: 'Built for How You Operate',
        body: 'Whether your vehicles deliver goods, transport equipment, or carry crews to job sites, we match the policy to your real-world use — including specialized vehicles and equipment.'
      },
      {
        title: 'Driver and Asset Protection',
        body: 'Beyond the vehicles themselves, we protect the people behind the wheel and the contents being transported, reducing financial exposure from accidents and incidents.'
      },
      {
        title: 'Scalable Fleet Solutions',
        body: 'Add or remove vehicles as your business changes. Our fleet programs are designed to flex with your growth.'
      }
    ]
  },

  'professional-liability': {
    slug: 'professional-liability',
    category: 'commercial',
    name: 'Professional Liability (Errors & Omissions)',
    tagline: 'Protection when your advice or services are called into question.',
    contactType: 'liability',
    heroImage: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80',
    intro: [
      "Professional liability insurance — also known as Errors and Omissions (E&O) — protects service-based businesses against claims of negligence, mistakes, missed deadlines, or failure to deliver services as promised.",
      "Whether you are a consultant, accountant, real estate professional, IT services provider, or any business that gives advice or delivers expertise, E&O is essential."
    ],
    features: [
      'Coverage for claims of professional negligence, errors, or omissions.',
      'Legal defense costs covered, even if a claim is groundless.',
      'Protection against allegations of breach of duty or contract performance issues.',
      'Tailored coverage by industry — consulting, real estate, accounting, IT, and more.',
      'Optional coverage extensions for specific industry risks.'
    ],
    sections: [
      {
        title: 'Defending Your Professional Reputation',
        body: 'A single dissatisfied client can file a lawsuit that threatens your business and reputation. E&O coverage helps you defend your work and absorb the financial impact.'
      },
      {
        title: 'Industry-Specific Protection',
        body: 'Different professions face different risks. We tailor E&O coverage to your industry so you are protected against the claims most likely to arise in your line of work.'
      },
      {
        title: 'A Mark of Professionalism',
        body: 'Carrying E&O coverage signals to clients that you take your work seriously and are prepared to stand behind it. In many industries, it is required by contract.'
      }
    ]
  },

  'cyber-risk': {
    slug: 'cyber-risk',
    category: 'commercial',
    name: 'Cyber & Specialized Risk Coverage',
    tagline: 'Protect your business against data breaches and emerging threats.',
    contactType: 'other',
    heroImage: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1200&q=80',
    intro: [
      "Cyberattacks, ransomware, data breaches, and phishing scams are no longer rare events — they are everyday business risks. Cyber liability insurance helps your business respond, recover, and continue operating after a digital incident.",
      "Beyond cyber, we also offer specialty coverages for unique exposures that standard policies do not address."
    ],
    features: [
      'Coverage for data breach response costs: notification, credit monitoring, and forensic investigation.',
      'Ransomware and cyber extortion protection.',
      'Business interruption coverage following a covered cyber event.',
      'Liability coverage for lawsuits arising from a breach.',
      'Specialty coverages available for unusual industry-specific risks.'
    ],
    sections: [
      {
        title: 'The New Frontier of Business Risk',
        body: 'Every business that stores customer data, processes payments, or operates online is a potential target. Cyber coverage gives you the resources and expertise to respond quickly and minimize damage.'
      },
      {
        title: 'Rapid Response When It Matters',
        body: 'After a cyber event, time is critical. Our partners provide immediate access to forensic specialists, legal counsel, and breach response professionals so you are not navigating it alone.'
      },
      {
        title: 'Specialty Risk Solutions',
        body: 'For businesses with unique exposures — high-value equipment, environmental risks, contractual requirements — we source specialty coverage to fill the gaps.'
      }
    ]
  },

  // ===== PERSONAL =====
  'auto': {
    slug: 'auto',
    category: 'personal',
    name: 'Auto Insurance',
    tagline: 'Protect yourself, your passengers, and your vehicle on every drive.',
    contactType: 'auto',
    heroImage: 'https://customer-assets.emergentagent.com/job_insure-your-future/artifacts/w6yobqqf_financial-independent-woman-buying-new-car.jpg',
    intro: [
      "Auto insurance is more than a legal requirement — it is the safety net that keeps you moving forward after an accident, theft, or unexpected event on the road. We help you find coverage that genuinely fits your driving habits, vehicle, and budget.",
      "From minimum liability to full protection, we walk you through every option so you can drive with confidence."
    ],
    features: [
      'Liability coverage for bodily injury and property damage you cause to others.',
      'Collision coverage for damage to your vehicle in an accident.',
      'Comprehensive coverage for theft, vandalism, weather, and non-collision events.',
      'Medical payments and personal injury protection (PIP).',
      'Uninsured and underinsured motorist protection.'
    ],
    sections: [
      {
        title: 'Coverage That Matches Your Driving',
        body: 'Daily commuter, occasional driver, or family with multiple vehicles — we structure your policy around how you actually use your car, not a one-size-fits-all formula.'
      },
      {
        title: 'Real Savings, Real Coverage',
        body: 'We shop multiple top-rated carriers to find competitive rates and bundle opportunities, often saving clients hundreds without sacrificing protection.'
      },
      {
        title: 'Always Here After a Claim',
        body: 'A claim is when insurance really matters. We stay involved through the claims process, helping you navigate paperwork and ensuring you get the support you deserve.'
      }
    ]
  },

  'homeowners': {
    slug: 'homeowners',
    category: 'personal',
    name: 'Homeowners & Renters Insurance',
    tagline: 'Protect the place you call home — whether you own it or rent it.',
    contactType: 'home',
    heroImage: 'https://images.unsplash.com/photo-1572120360610-d971b9d7767c?w=1200&q=80',
    intro: [
      "Your home is more than four walls — it is where life happens. Homeowners insurance protects your dwelling, your belongings, and your financial security against fire, theft, storms, and liability claims.",
      "Renting? Renters insurance protects your personal property and provides liability coverage at a surprisingly low cost. Either way, we help you secure the place you call home."
    ],
    features: [
      'Dwelling coverage for the structure of your home and attached structures.',
      'Personal property coverage for your belongings, inside and outside the home.',
      'Liability protection if someone is injured on your property.',
      'Additional living expenses if a covered loss makes your home temporarily uninhabitable.',
      'Renters coverage tailored to apartment, condo, and rental living.'
    ],
    sections: [
      {
        title: 'Florida-Smart Coverage',
        body: 'Florida homeowners face unique risks — wind, hail, and water. We help you understand your policy, deductibles, and coverage limits so you are not caught off guard during storm season.'
      },
      {
        title: 'Affordable Renters Protection',
        body: 'Renters insurance is one of the best values in personal insurance. For a small monthly cost, you get coverage for your belongings, liability protection, and temporary living expenses.'
      },
      {
        title: 'Bundle and Save',
        body: 'Combining home and auto coverage with the same carrier often unlocks significant discounts. We make sure you are not leaving savings on the table.'
      }
    ]
  },

  'flood': {
    slug: 'flood',
    category: 'personal',
    name: 'Flood Insurance',
    tagline: 'Standard home policies do not cover flood. We help you fill that gap.',
    contactType: 'flood',
    heroImage: 'https://images.unsplash.com/photo-1547683905-f686c993aae5?w=1200&q=80',
    intro: [
      "Flood damage is one of the most common and costly disasters in Florida — yet it is excluded from nearly every standard homeowners policy. Dedicated flood insurance is the only way to truly protect your home and belongings from rising water.",
      "Whether your property is in a designated flood zone or not, we help you understand your risk and secure the right coverage."
    ],
    features: [
      'Coverage for damage to your building structure from flooding.',
      'Coverage for personal contents and belongings inside your home.',
      'Available through the National Flood Insurance Program (NFIP) and private market.',
      'Required by most mortgage lenders in high-risk flood zones.',
      'Affordable options even in low-to-moderate risk areas.'
    ],
    sections: [
      {
        title: 'Florida Flood Risk Is Real',
        body: 'Hurricanes, tropical storms, heavy rain, and rising sea levels make flood risk a year-round concern. Even properties outside flood zones can experience flooding.'
      },
      {
        title: 'NFIP vs. Private Market',
        body: 'We help you compare federal NFIP policies against private flood carriers, finding coverage that matches your home, location, and budget.'
      },
      {
        title: 'Peace of Mind Before the Storm',
        body: 'There is typically a 30-day waiting period for new flood policies to take effect. We help you plan ahead so you are not scrambling when storms are forecast.'
      }
    ]
  },

  'umbrella': {
    slug: 'umbrella',
    category: 'personal',
    name: 'Umbrella & Extended Protection',
    tagline: 'Extra liability protection that goes beyond your home and auto limits.',
    contactType: 'umbrella',
    heroImage: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=1200&q=80',
    intro: [
      "Umbrella insurance provides an additional layer of liability coverage on top of your existing home and auto policies. When the unexpected happens — a serious accident, a guest injury, a lawsuit — umbrella coverage helps protect your assets and your future income.",
      "It is one of the most affordable ways to add meaningful protection to your overall insurance program."
    ],
    features: [
      'Additional liability coverage above your home and auto policy limits.',
      'Covers bodily injury, property damage, and certain personal injury claims.',
      'Legal defense costs covered, even for claims the underlying policies might exclude.',
      'Worldwide coverage on most policies.',
      'Affordable premiums for $1 million or more in extra protection.'
    ],
    sections: [
      {
        title: 'A Single Lawsuit Can Change Everything',
        body: 'Serious accidents can result in claims well beyond standard policy limits. Umbrella coverage steps in once your base liability is exhausted, protecting your savings, investments, and future income.'
      },
      {
        title: 'Who Needs Umbrella Coverage?',
        body: 'Families with teen drivers, pool owners, dog owners, frequent hosts, and anyone with significant assets benefit most. If you have something to protect, umbrella coverage is worth a conversation.'
      },
      {
        title: 'Surprisingly Affordable',
        body: 'For typical households, $1 million in umbrella coverage often costs just a few hundred dollars per year — outstanding value for the protection it provides.'
      }
    ]
  }
};

export const getProductBySlug = (slug) => products[slug];

export const getProductsByCategory = (category) =>
  Object.values(products).filter((p) => p.category === category);
